import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useResolvedImageUrl } from "../../hooks/useResolvedImageUrl";
import { useAnalytics } from "../../lib/analytics";
import { getCardThumbnailClass } from "../../lib/edhub/cardThumbnails";
import { formatToSegment } from "../../lib/edhub/formatToSegment";
import { useClientTranslations } from "../../lib/i18n/client";
import type {
	ContentFormat,
	EducationContent,
} from "../../services/educationService";
import styles from "./ContentCard.module.css";
import { ViewAsDropdown } from "./ViewAsDropdown";

const RECOMMENDED_BACKGROUNDS = [
	"/images/education-recommended-1.png",
	"/images/education-recommended-2.png",
];

interface ContentCardProps {
	content: EducationContent;
	index?: number;
}

export function ContentCard({ content, index = 0 }: ContentCardProps) {
	const { logEducation } = useAnalytics();
	const { setPrefillQuestion } = useChatActions();
	const navigate = useNavigate();
	const { t, direction } = useClientTranslations("education");
	const [isBookmarked, setIsBookmarked] = useState(content.isBookmarked);
	// A purpose-framed 105×244 thumbnail, where one exists, beats cropping the
	// landscape hero down to the card's portrait slot. The asset is carried by a
	// CSS-module class, so there is nothing to bearer-fetch and no skeleton.
	const thumbnailClass = getCardThumbnailClass(content.id);
	// Auth-gated proxy images can't load via a headerless CSS url(); resolve to a
	// bearer-fetched blob (presigned URLs pass through). Skipped entirely when a
	// static thumbnail already covers this card.
	// loading → neutral skeleton (no decoy photo flash)
	// error/idle → realistic rotation fallback
	// loaded → hero image
	const { url: heroUrl, status: heroStatus } = useResolvedImageUrl(
		thumbnailClass ? undefined : content.imageUrl,
	);

	const handleAskEve = useCallback(() => {
		logEducation("card_ask_eve_pressed", {
			contentId: content.id,
		});
		setPrefillQuestion(content.title);
		navigate("/chat");
	}, [logEducation, content.id, setPrefillQuestion, content.title, navigate]);

	const handleBookmarkToggle = useCallback(() => {
		setIsBookmarked((prev) => {
			logEducation("card_bookmark_toggled", {
				contentId: content.id,
				isBookmarked: !prev,
			});
			return !prev;
		});
	}, [logEducation, content.id]);

	const handleShare = useCallback(() => {
		toast.info(t("actions.shareComingSoon"));
		logEducation("card_share_pressed", {
			contentId: content.id,
		});
	}, [t, logEducation, content.id]);

	const handleFormatSelect = useCallback(
		(format: ContentFormat) => {
			logEducation("card_view_as_selected", {
				contentId: content.id,
				format,
			});
			navigate(`/education/${content.id}/${formatToSegment(format)}`);
		},
		[logEducation, content.id, navigate],
	);

	const handleCardClick = useCallback(() => {
		logEducation("card_opened", { contentId: content.id });
		// Default to the cards view, but only when this piece actually has cards
		// (real content always does; the MOCK_RECOMMENDED fallback does not).
		// Otherwise fall back to the article view rather than landing on a
		// cards page that has nothing to load.
		const defaultFormat: ContentFormat = content.availableFormats.includes(
			"cards",
		)
			? "cards"
			: "text";
		navigate(`/education/${content.id}/${formatToSegment(defaultFormat)}`);
	}, [logEducation, content.id, content.availableFormats, navigate]);

	return (
		<div className={styles.card} dir={direction}>
			<div
				className={`${styles.thumbnailArea}${thumbnailClass ? ` ${styles[thumbnailClass]}` : heroStatus === "loading" ? ` ${styles.thumbnailSkeleton}` : ""}`}
				style={
					thumbnailClass
						? undefined
						: heroStatus === "loading"
							? // Neutral skeleton: no background-image, CSS handles the shimmer.
								undefined
							: heroStatus === "loaded" && heroUrl
								? {
										// Hero image first, rotation fallback second — if the hero URL
										// fails to load, the fallback layer beneath it still renders.
										backgroundImage: `url(${heroUrl}), url(${RECOMMENDED_BACKGROUNDS[index % RECOMMENDED_BACKGROUNDS.length]})`,
									}
								: {
										// error or idle → realistic rotation fallback.
										backgroundImage: `url(${RECOMMENDED_BACKGROUNDS[index % RECOMMENDED_BACKGROUNDS.length]})`,
									}
				}
				aria-hidden="true"
			/>
			<div className={styles.info}>
				<div className={styles.actionsRow}>
					<button
						type="button"
						className={styles.actionButton}
						onClick={handleAskEve}
						title={t("actions.askEve")}
					>
						<img
							src="/images/education/icon-ask-eve.svg"
							alt={t("actions.askEve")}
							className={styles.actionIcon}
						/>
					</button>
					<button
						type="button"
						className={`${styles.actionButton} ${isBookmarked ? styles.bookmarkActive : ""}`}
						onClick={handleBookmarkToggle}
						title={t(isBookmarked ? "actions.bookmarked" : "actions.bookmark")}
					>
						<img
							src={
								isBookmarked
									? "/images/education/icon-heart-filled.svg"
									: "/images/education/icon-heart.svg"
							}
							alt={t(isBookmarked ? "actions.bookmarked" : "actions.bookmark")}
							className={styles.actionIcon}
						/>
					</button>
					<button
						type="button"
						className={styles.actionButton}
						onClick={handleShare}
						title={t("actions.share")}
					>
						<img
							src="/images/education/icon-share.svg"
							alt={t("actions.share")}
							className={styles.actionIcon}
						/>
					</button>
				</div>
				<button
					type="button"
					className={styles.textContent}
					onClick={handleCardClick}
				>
					<h3 className={styles.title}>{content.title}</h3>
					<p className={styles.description}>
						{content.descriptionKey
							? t(content.descriptionKey, content.description)
							: content.description}
					</p>
				</button>
				<ViewAsDropdown
					availableFormats={content.availableFormats}
					onFormatSelect={handleFormatSelect}
					openDirection="up"
				/>
			</div>
		</div>
	);
}
